import React, { useState, useMemo } from 'react';
import { PriceDisplay } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Toggle } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { List } from '@heartinternet/hi-ui';
import { ListItem } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import type { Product } from '../types';

interface BusinessEmailCardProps {
  product: Product;
  formAction: string;
  isReseller?: boolean;
}

export const BusinessEmailCard: React.FC<BusinessEmailCardProps> = ({
  product,
  formAction,
  isReseller,
}) => {
  const tiers = product.tiers!;

  const [selectedTierId, setSelectedTierId] = useState(tiers[0].id);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');

  const selectedTier = useMemo(
    () => tiers.find((t) => t.id === selectedTierId)!,
    [tiers, selectedTierId]
  );

  // Quantities come from the selected tier, falling back to product-level
  const quantities = useMemo(
    () => selectedTier.quantities || product.quantities || [],
    [selectedTier, product.quantities]
  );
  const [selectedQuantity, setSelectedQuantity] = useState(quantities[0]?.count.toString() || '1');

  // Reset quantity when tier changes if current selection doesn't exist in new tier
  const effectiveQuantity = useMemo(() => {
    if (quantities.find((q) => q.count.toString() === selectedQuantity)) {
      return selectedQuantity;
    }
    return quantities[0]?.count.toString() || '1';
  }, [quantities, selectedQuantity]);

  const selectedQty = useMemo(
    () => quantities.find((q) => q.count.toString() === effectiveQuantity),
    [quantities, effectiveQuantity]
  );

  const hasYearly = tiers.some((t) => !!t.yearlyPrice) || quantities.some((q) => !!q.yearlyPrice);

  const displayPrice = useMemo(() => {
    let priceInfo;

    if (billingInterval === 'yearly') {
      priceInfo = selectedQty?.yearlyPrice || selectedTier.yearlyPrice;
    }
    if (!priceInfo) {
      priceInfo = selectedQty?.price || selectedTier.price;
    }

    if (isReseller && priceInfo.resellerAmount != null) {
      return priceInfo.resellerAmount;
    }
    return priceInfo.amount;
  }, [selectedQty, selectedTier, isReseller, billingInterval]);

  const displayInterval = billingInterval === 'yearly' ? ('year' as const) : ('month' as const);

  const renewalNote = useMemo(() => {
    if (!selectedQty) return null;
    const renewal =
      billingInterval === 'yearly' ? selectedQty.yearlyRenewalPrice : selectedQty.renewalPrice;
    if (!renewal) return null;

    const currency = selectedTier.price.currency || '£';
    const intervalLabel = billingInterval === 'yearly' ? 'year' : 'month';

    return `for the first ${intervalLabel}, then ${currency}${renewal.amount.toFixed(2)}/${intervalLabel}`;
  }, [selectedQty, selectedTier, billingInterval]);

  const basketAddCode = useMemo(() => {
    return `${selectedTier.basketAddCodePrefix}/${billingInterval}/card/${effectiveQuantity}`;
  }, [selectedTier, billingInterval, effectiveQuantity]);

  return (
    <div className="hi-ui-extras-page__product-content">
      <Text variant="body" inline>
        {product.description}
      </Text>
      {tiers.length > 1 ? (
        <div className="hi-ui-extras-page__tier-columns">
          {tiers.map((tier) => {
            const isActive = tier.id === selectedTierId;
            return (
              <button
                key={tier.id}
                type="button"
                className={`hi-ui-extras-page__tier-column${isActive ? ' hi-ui-extras-page__tier-column--active' : ''}`}
                onClick={() => setSelectedTierId(tier.id)}
                aria-pressed={isActive}
              >
                <Text variant="heading-4">{tier.name}</Text>
                <List className="hi-ui-extras-page__features hi-ui-extras-page__features--icons">
                  {tier.features.map((text, i) => (
                    <ListItem key={i}>
                      <Icon name="check-circle-grey" size={18} />
                      {text}
                    </ListItem>
                  ))}
                </List>
              </button>
            );
          })}
        </div>
      ) : (
        <List className="hi-ui-extras-page__features hi-ui-extras-page__features--icons">
          {selectedTier.features.map((text, i) => (
            <ListItem key={i}>
              <Icon name="check-circle-grey" size={18} />
              {text}
            </ListItem>
          ))}
        </List>
      )}
      <Form action={formAction} method="post">
        <input type="hidden" name="type" value="MULTI" />
        <input type="hidden" name="submit-type" value="PURCHASE" />
        <input type="hidden" name="basket-add-code" value={basketAddCode} />

        <Card variant="surface" padding="md" className="hi-ui-extras-page__card-form">
          {quantities.length > 1 && (
            <FormField label="Mailboxes" fullWidth>
              <Select
                value={effectiveQuantity}
                onChange={(e) => setSelectedQuantity(e.target.value)}
                fullWidth
              >
                {quantities.map((qty) => (
                  <option key={qty.count} value={qty.count}>
                    {qty.label}
                  </option>
                ))}
              </Select>
            </FormField>
          )}

          {hasYearly && (
            <div className="hi-ui-extras-page__billing-toggle">
              <Text variant="body">Monthly</Text>
              <Toggle
                checked={billingInterval === 'yearly'}
                onChange={(e) => setBillingInterval(e.target.checked ? 'yearly' : 'monthly')}
                aria-label="Billing period"
              />
              <Text variant="body">Yearly</Text>
            </div>
          )}

          <div className="hi-ui-extras-page__card-footer">
            <div className="hi-ui-extras-page__price-with-renewal">
              <PriceDisplay
                amount={displayPrice}
                currency={selectedTier.price.currency}
                interval={renewalNote ? undefined : displayInterval}
                intervalFormat="long"
                size="lg"
              />
              {renewalNote && (
                <Text variant="body-small" className="hi-ui-extras-page__renewal-note">
                  {renewalNote}
                </Text>
              )}
            </div>
            <Button type="submit" variant="primary">
              Add to basket
            </Button>
          </div>
        </Card>
      </Form>
    </div>
  );
};
